import { useEffect } from 'react'

type SeoInput = {
  /** Document title */
  title: string
  /** Meta description */
  description?: string
  /**
   * Social share image URL (prefer absolute, but relative is OK).
   * Note: for best compatibility, use a JPG/PNG (not WebP).
   */
  image?: string
}

function upsertMetaByName(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${CSS.escape(name)}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertMetaByProperty(property: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(
    `meta[property="${CSS.escape(property)}"]`,
  )
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function toAbsoluteUrl(maybeRelativeUrl: string) {
  try {
    return new URL(maybeRelativeUrl, window.location.origin).toString()
  } catch {
    return maybeRelativeUrl
  }
}

/**
 * Client-side SEO/OG helper.
 *
 * Important: Many link unfurlers do NOT execute JS on SPAs.
 * This improves in-app meta + any crawlers that execute JS, but for guaranteed
 * unfurls you’ll want SSR or prerender/static meta per route.
 */
export function useSeo({ title, description, image }: SeoInput) {
  useEffect(() => {
    if (title) document.title = title

    const desc = description?.trim()
    if (desc) {
      upsertMetaByName('description', desc)
      upsertMetaByProperty('og:description', desc)
      upsertMetaByName('twitter:description', desc)
    }

    if (title) {
      upsertMetaByProperty('og:title', title)
      upsertMetaByName('twitter:title', title)
    }

    upsertMetaByProperty('og:type', 'website')
    upsertMetaByProperty('og:url', window.location.href)
    upsertMetaByName('twitter:card', 'summary_large_image')

    if (image) {
      const abs = toAbsoluteUrl(image)
      upsertMetaByProperty('og:image', abs)
      upsertMetaByName('twitter:image', abs)
    }
  }, [title, description, image])
}

